import { format } from "date-fns";
import { Calendar, CircleQuestionMark, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { Card } from "@/components/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button, buttonVariants } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { PublicPlaylist, PublicRecording } from "@classview/shared-types";

import { useMarkRecordingAsPermanent } from "../api/mark-recording-as-permanent";

import { RecordingThumbnail } from "./recording-thumbnail";

type RecordingItemProps = {
  recording: PublicRecording;
  playlist?: PublicPlaylist;
};

function getMonogram(hostName: string) {
  return hostName
    .split(" ")
    .map((name) => name[0])
    .join("");
}

export function RecordingItem({ recording, playlist }: RecordingItemProps) {
  const recordingUrl = new URLSearchParams();
  const {
    mutateAsync: markRecordingAsPermanent,
    isPending: isMarkingRecordingAsPermanent,
    error: errorMarkingRecordingAsPermanent,
  } = useMarkRecordingAsPermanent();

  if (playlist) {
    recordingUrl.set("playlistId", playlist.id);
  }

  const handleMarkRecordingAsPermanent = async () => {
    toast.promise(
      markRecordingAsPermanent({
        id: recording.id,
        orgId: recording.organisation.id,
      }),
      {
        loading: "Marking recording as permanent...",
        success: "Recording marked as permanent",
        error: errorMarkingRecordingAsPermanent?.message,
      },
    );
  };

  const formattedDate = format(recording.date, "PP");

  const isExpired =
    recording.daysLeftToDelete != null
      ? recording.daysLeftToDelete <= 0 &&
        !recording.isVisible &&
        !recording.isPermanent
      : false;

  const isHiddenBecauseOfStorageLimit = Boolean(
    recording.daysLeftToDelete &&
      recording.daysLeftToDelete > 0 &&
      !recording.isVisible,
  );

  return (
    <TooltipProvider>
      <Card className={cn("flex flex-1 flex-col")} as="article">
        <Link
          className={cn(
            "dark group relative block",
            !recording.isVisible && "pointer-events-none opacity-50",
          )}
          to={`/recordings/${recording.id}?${recordingUrl.toString()}`}
          onClick={(e) => {
            if (!recording.isVisible) {
              e.preventDefault();
            }
          }}
        >
          <RecordingThumbnail imageURL={recording.imageURL} />
        </Link>
        <Card.Content className="h-full flex-1 pt-3">
          <div
            className={cn(
              "text-md font-semibold text-foreground",
              !recording.isVisible && "select-none opacity-50",
            )}
          >
            {recording.name}{" "}
            {recording.subtitle ? `(${recording.subtitle})` : ""}
          </div>
          <div className="mb-5">
            <div className="flex flex-row items-center justify-between">
              <dl className="flex flex-row items-center space-x-3">
                <div
                  className={cn(
                    "flex items-center space-x-3",
                    !recording.isVisible && "select-none opacity-50",
                  )}
                >
                  <div className="flex items-center">
                    <dt className="sr-only">Duration</dt>
                    <dd className="flex items-center text-sm text-muted">
                      <Clock
                        className="mr-1 size-3 text-card-foreground"
                        aria-hidden={true}
                      />
                      {recording.duration}
                    </dd>
                  </div>
                  <div className="flex items-center">
                    <dt className="sr-only">Recording date</dt>
                    <dd
                      className="flex items-center text-sm text-muted"
                      title={`Expiration date: ${recording.expirationDate ? format(recording.expirationDate, "PP") : "Permanent"}`}
                    >
                      <Calendar
                        className="mr-1 size-3 text-card-foreground"
                        aria-hidden={true}
                      />
                      {formattedDate}
                    </dd>
                  </div>
                </div>
              </dl>
              {/* {recording.isVisible && (
                <RecordingItemActions
                  recordingId={recording.id}
                  playlist={playlist}
                />
              )} */}
            </div>
            {isExpired || isHiddenBecauseOfStorageLimit ? (
              <Tooltip delayDuration={0} key="expired-recording">
                <TooltipTrigger asChild>
                  <span className="inline-flex items-center gap-2">
                    {isExpired && (
                      <Badge variant="destructive" className="select-none">
                        Expired
                      </Badge>
                    )}
                    {isHiddenBecauseOfStorageLimit && (
                      <Badge variant="destructive" className="select-none">
                        Storage is full
                      </Badge>
                    )}
                    <CircleQuestionMark className="size-4 text-muted" />
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  {isExpired && (
                    <>
                      <span className="text-md font-semibold">
                        This recording has expired
                      </span>
                      <p className="text-sm text-muted">
                        Your permanent storage is not full, you can mark this
                        recording as permanent and user will be able to access
                        it.
                      </p>
                      <Button
                        className="my-2"
                        onClick={handleMarkRecordingAsPermanent}
                        disabled={isMarkingRecordingAsPermanent}
                      >
                        Mark as permanent
                      </Button>
                    </>
                  )}

                  {isHiddenBecauseOfStorageLimit && (
                    <>
                      <span className="text-md font-semibold">
                        Your storage is full
                      </span>
                      <p className="mb-2 text-sm text-muted">
                        Contact customer service to expand your storage to
                        re-enable access to this replay.
                      </p>

                      <a
                        href="https://classview.com/contact-us?utm_source=classview-license-page&utm_medium=referral"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={cn(
                          buttonVariants({ variant: "outline" }),
                          "ml-auto",
                        )}
                      >
                        Contact Sales
                      </a>
                    </>
                  )}
                </TooltipContent>
              </Tooltip>
            ) : null}
          </div>
          <div
            className={cn(
              "flex items-start space-x-2",
              !recording.isVisible && "select-none opacity-50",
            )}
          >
            <Avatar>
              <AvatarImage src={recording.host.avatarURL || undefined} />
              <AvatarFallback className="bg-border capitalize text-muted">
                {getMonogram(recording.host.name)}
              </AvatarFallback>
            </Avatar>
            <div className="flex flex-col">
              <p className="text-sm text-muted">{recording.host.name}</p>
              <p className="text-sm font-medium text-foreground">
                {recording.organisation.name}
              </p>
            </div>
          </div>
        </Card.Content>
      </Card>
    </TooltipProvider>
  );
}
